// Single source of truth for which areas the admin Overview dashboard aggregates,
// each keyed to the permission its target screen requires (mirrors the route
// guards in App.tsx). Shared by Overview.tsx (to gate the cards/actions it
// renders) and Layout.tsx (to decide whether the Overview nav entry is worth
// showing). Keeping both in lock-step is what stops a single-permission role
// from seeing an Overview full of controls it can't use.

export interface OverviewArea {
  key: string
  /** Permission required to see this area's card/action AND to reach its screen. */
  perm: string
}

// Content areas that carry a real card or quick-action. Platform Info is static
// chrome (no action, no data) and is intentionally NOT listed here, so it never
// on its own justifies showing the aggregate Overview.
export const OVERVIEW_AREAS: OverviewArea[] = [
  { key: 'metrics', perm: 'analytics.read' },
  { key: 'workspaces', perm: 'apps.read' },
  { key: 'users', perm: 'users.read' },
  { key: 'claudeCode', perm: 'config.read' },
  { key: 'testNotifications', perm: 'notifications.write' },
]

/** How many Overview content areas the user can actually see. */
export function visibleOverviewAreaCount(can: (perm: string) => boolean): number {
  return OVERVIEW_AREAS.filter((area) => can(area.perm)).length
}

/**
 * Whether the aggregate Overview is worth showing this user. Admins always see
 * it. Everyone else sees it only when they can open at least two of its areas —
 * a single-area grantee gets sent straight to that one screen instead, since an
 * "overview" of one thing is just that thing.
 */
export function shouldShowOverview(isAdmin: boolean, can: (perm: string) => boolean): boolean {
  return isAdmin || visibleOverviewAreaCount(can) >= 2
}
